import type {
  IAnalyticsEventEnvelope,
  IRedactAnalyticsEventProps,
  IRedactAnalyticsUrlProps,
  IRedactAnalyticsValueProps,
  ISecretPathSegment,
} from "./product-analytics.types";

// NOTE: NSAI-6105 / NSAI-6118 — the single redactor both clients run. Two
// findings drove it, and they are the reason it is shaped this way rather than
// as a tidier "sanitise the path we pass to capture":
//
//   1. Web's `capturePageview` concatenated `location.search`, so a
//      password-reset / invite token reached `$current_url` and `pathname`.
//   2. Both SDKs then republish a route they captured ONCE onto every later
//      event — web as `$session_entry_url`, mobile as the sticky `$screen_name`
//      super property. Sanitising our own call sites leaves those untouched,
//      which is why this also runs as a `before_send` hook: that is the only
//      place an SDK-generated property can be caught.
//
// The tracking plan (`docs/observability/posthog-tracking-plan.md`) forbids
// sending access tokens, API keys and signed URLs at all — this file is where
// that rule is enforced rather than restated.
export const ANALYTICS_REDACTED = "[redacted]";

// NOTE: Query parameters kept verbatim. An ALLOWLIST, not a denylist of
// credential names: a denylist only protects against the parameter names
// someone thought of, and the next credential-bearing parameter will not be on
// it. Attribution is the only thing analytics genuinely needs out of a query
// string, so it is the only thing that survives.
const SAFE_QUERY_PARAMS: ReadonlySet<string> = new Set([
  "gclid",
  "fbclid",
  "ref",
]);

// NOTE: The whole `utm_` family, matched by prefix rather than by an exact list.
// GA4 keeps adding members (`utm_id`, `utm_source_platform`,
// `utm_marketing_tactic`, `utm_creative_format`), and an exact list silently
// drops campaign attribution for every one it has not been updated for. Safe to
// widen this way because the prefix is an analytics convention — nothing mints a
// credential called `utm_*`.
const SAFE_QUERY_PARAM_PREFIX = "utm_";

// NOTE: Path prefixes whose NEXT segment is a bearer capability, not an id.
// Replaced with the route template, which is both the safe value and the more
// useful one: screens aggregate instead of fragmenting one row per token.
const SECRET_PATH_SEGMENTS: readonly ISecretPathSegment[] = [
  { prefix: "/shared/chat/", placeholder: "[shareToken]" },
  { prefix: "/shared/public-chat/", placeholder: "[shareToken]" },
];

// NOTE: Matches an absolute URL (`https://host/path`) or a scheme-relative one.
// Anything else is treated as a bare path, which is what both providers pass.
const ABSOLUTE_URL_PATTERN = /^[a-z][a-z0-9+.-]*:\/\//i;

const redactQueryString = ({ search }: { search: string }): string => {
  if (!search) return "";

  const redacted = search
    .split("&")
    .filter((pair) => pair.length > 0)
    .map((pair) => {
      const separatorIndex = pair.indexOf("=");
      if (separatorIndex === -1) return pair;

      const name = pair.slice(0, separatorIndex).toLowerCase();
      if (SAFE_QUERY_PARAMS.has(name)) return pair;
      if (name.startsWith(SAFE_QUERY_PARAM_PREFIX)) return pair;

      return `${name}=${ANALYTICS_REDACTED}`;
    })
    .join("&");

  return redacted ? `?${redacted}` : "";
};

const redactPathname = ({ pathname }: { pathname: string }): string => {
  const match = SECRET_PATH_SEGMENTS.find((segment) =>
    pathname.startsWith(segment.prefix),
  );
  if (!match) return pathname;

  const rest = pathname.slice(match.prefix.length);
  if (!rest) return pathname;

  // NOTE: Only the token segment is replaced — anything nested below it is a
  // real route and keeps its shape.
  const nextSeparatorIndex = rest.indexOf("/");
  const tail = nextSeparatorIndex === -1 ? "" : rest.slice(nextSeparatorIndex);

  return `${match.prefix}${match.placeholder}${tail}`;
};

// NOTE: Takes an absolute URL or a bare path and returns it with secret path
// segments templated and every non-attribution query value redacted. The
// fragment is dropped outright: nothing in the tracking plan needs it, and it
// is another place a token can ride along.
export const redactAnalyticsUrl = ({
  url,
}: IRedactAnalyticsUrlProps): string => {
  if (!url) return url;

  const fragmentIndex = url.indexOf("#");
  const withoutFragment =
    fragmentIndex === -1 ? url : url.slice(0, fragmentIndex);

  const queryIndex = withoutFragment.indexOf("?");
  const beforeQuery =
    queryIndex === -1 ? withoutFragment : withoutFragment.slice(0, queryIndex);
  const search = queryIndex === -1 ? "" : withoutFragment.slice(queryIndex + 1);

  if (!ABSOLUTE_URL_PATTERN.test(beforeQuery)) {
    return `${redactPathname({ pathname: beforeQuery })}${redactQueryString({ search })}`;
  }

  // NOTE: Split origin from path by hand rather than through `URL`, so this
  // stays dependency-free and behaves the same in Hermes and in a browser.
  const schemeEndIndex = beforeQuery.indexOf("://") + "://".length;
  const pathStartIndex = beforeQuery.indexOf("/", schemeEndIndex);
  if (pathStartIndex === -1) {
    return `${beforeQuery}${redactQueryString({ search })}`;
  }

  const origin = beforeQuery.slice(0, pathStartIndex);
  const pathname = beforeQuery.slice(pathStartIndex);

  return `${origin}${redactPathname({ pathname })}${redactQueryString({ search })}`;
};

// NOTE: Whether a value is worth running through the URL redactor. Deliberately
// shape-based rather than a list of known property names: `$session_entry_url`
// was missed precisely because nobody enumerated it, and the next SDK release
// can add another one without telling us.
const getIsLocationLike = ({ value }: { value: string }): boolean =>
  value.startsWith("/") || ABSOLUTE_URL_PATTERN.test(value);

export const redactAnalyticsValue = ({
  value,
}: IRedactAnalyticsValueProps): string =>
  getIsLocationLike({ value }) ? redactAnalyticsUrl({ url: value }) : value;

// NOTE: The `before_send` body, kept SDK-agnostic so web and mobile can each
// adapt it to their own hook signature — the same split as
// `createProductAnalyticsClient`, which takes the SDK rather than importing it.
// Returns the event so a caller can pass it straight through; never drops one,
// because a dropped event is a silently missing funnel step.
export const redactAnalyticsEvent = ({
  event,
}: IRedactAnalyticsEventProps): IAnalyticsEventEnvelope | null => {
  if (!event?.properties) return event;

  const properties = event.properties;
  for (const key of Object.keys(properties)) {
    const value = properties[key];
    if (typeof value !== "string") continue;

    const redacted = redactAnalyticsValue({ value });
    if (redacted !== value) {
      properties[key] = redacted;
    }
  }

  return event;
};
